import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpRequest } from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Single service for the admin screens added on top of the legacy compat API:
 * content (announcements / banner / partner), news, medal-tally, QR attendance,
 * PlayZone quiz builder, Media Vault manager and subscribers. The AuthInterceptor
 * attaches the bearer token, so these just hit the endpoints.
 */
@Injectable({ providedIn: 'root' })
export class AdminFeaturesService {
  private base = environment.baseUrl; // e.g. http://localhost:4000/api/  (prod: /api/)

  constructor(private http: HttpClient) {}

  private err(e: HttpErrorResponse) { return throwError(e); }
  private get<T>(p: string)            { return this.http.get<T>(this.base + p).pipe(catchError(this.err)); }
  private post<T>(p: string, b: any)   { return this.http.post<T>(this.base + p, b).pipe(catchError(this.err)); }
  private put<T>(p: string, b: any)    { return this.http.put<T>(this.base + p, b).pipe(catchError(this.err)); }
  private patch<T>(p: string, b: any)  { return this.http.patch<T>(this.base + p, b).pipe(catchError(this.err)); }
  private del<T>(p: string)            { return this.http.delete<T>(this.base + p).pipe(catchError(this.err)); }

  // Shared dropdown sources (legacy compat endpoints)
  eventList()  { return this.get<any[]>('event/eventList'); }
  gameList()   { return this.get<any[]>('game/gameList'); }
  schoolList() { return this.get<any[]>('api/schoolList'); }

  // ── Student transfers ───────────────────────────────────────────────────────
  // `status=action` is the queue that actually needs ISSO: everything else is
  // shown for context, not for a decision.
  listTransfers(status?: string)   { return this.get<any[]>(`admin/transfers${status ? `?status=${status}` : ''}`); }
  getTransfer(id: number)          { return this.get<any>(`admin/transfers/${id}`); }
  /** One student's transfer history, keyed on the ISSO id so it spans schools. */
  transfersForStudent(uniqueId: string) {
    return this.get<any[]>(`admin/transfers?uniqueId=${encodeURIComponent(uniqueId)}`);
  }
  cancelTransfer(id: number)       { return this.post(`admin/transfers/${id}/cancel`, {}); }
  /** ISSO raising a transfer on a family's behalf - goes straight to the new school. */
  raiseTransfer(b: any)            { return this.post('admin/transfers', b); }
  /** Schools to choose from, by name. */
  searchSchoolsPublic(q: string)   { return this.get<any[]>(`public/schools?q=${encodeURIComponent(q)}`); }

  // ── Content: announcements / dashboard banner / today's partner ──────────────
  siteNotice()                 { return this.get<any>('admin/content/site-notice'); }
  saveSiteNotice(b: any)       { return this.put<any>('admin/content/site-notice', b); }
  listAnnouncements()          { return this.get<any[]>('admin/content/announcements'); }
  saveAnnouncement(b: any)     { return this.post('admin/content/announcements', b); }
  updateAnnouncement(id, b)    { return this.put(`admin/content/announcements/${id}`, b); }
  deleteAnnouncement(id)       { return this.del(`admin/content/announcements/${id}`); }

  listBanners()                { return this.get<any[]>('admin/content/dashboard-banners'); }
  saveBanner(b: any)           { return this.post('admin/content/dashboard-banners', b); }
  updateBanner(id, b)          { return this.put(`admin/content/dashboard-banners/${id}`, b); }
  deleteBanner(id)             { return this.del(`admin/content/dashboard-banners/${id}`); }

  listPartners()               { return this.get<any[]>('admin/content/partner-highlights'); }
  savePartner(b: any)          { return this.post('admin/content/partner-highlights', b); }
  updatePartner(id, b)         { return this.put(`admin/content/partner-highlights/${id}`, b); }
  deletePartner(id)            { return this.del(`admin/content/partner-highlights/${id}`); }

  // ── Live streams (YouTube telecast per event) ────────────────────────────────
  listLiveStreams()            { return this.get<any[]>('admin/content/live-streams'); }
  saveLiveStream(b: any)       { return this.post('admin/content/live-streams', b); }
  updateLiveStream(id, b)      { return this.put(`admin/content/live-streams/${id}`, b); }
  deleteLiveStream(id)         { return this.del(`admin/content/live-streams/${id}`); }

  /** Upload a content image (sponsor logo / banner / partner art) → { url }. */
  uploadContentImage(file: File) {
    const fd = new FormData();
    fd.append('file', file);
    return this.http.post<{ url: string }>(this.base + 'admin/content/upload', fd).pipe(catchError(this.err));
  }

  /** Flip the visibility flag from the table's status badge. */
  setContentActive(collection: string, id: number, isActive: boolean) {
    return this.patch(`admin/content/${collection}/${id}/active`, { isActive });
  }

  // ── Sponsors (footer strip: logo + name + partner type, max 8 active) ────────
  listSponsors()               { return this.get<any[]>('admin/content/sponsors'); }
  saveSponsor(b: any)          { return this.post('admin/content/sponsors', b); }
  updateSponsor(id, b)         { return this.put(`admin/content/sponsors/${id}`, b); }
  deleteSponsor(id)            { return this.del(`admin/content/sponsors/${id}`); }

  // ── News ─────────────────────────────────────────────────────────────────────
  listNews()                   { return this.get<any[]>('news/admin'); }
  saveNews(b: any)             { return this.post('news', b); }
  updateNews(id, b)            { return this.put(`news/${id}`, b); }
  deleteNews(id)               { return this.del(`news/${id}`); }
  /** Post image - returns { url } to store on the post. */
  uploadNewsImage(fd: FormData) {
    return this.http.post<{ url: string }>(this.base + 'news/upload-image', fd).pipe(catchError(this.err));
  }
  /** Downloadable attachment - returns the storage key + original filename. */
  uploadNewsFile(fd: FormData) {
    return this.http.post<{ attachmentKey: string; attachmentName: string }>(
      this.base + 'news/upload-file', fd).pipe(catchError(this.err));
  }
  /** Announcement attachment - same shape as the news one. */
  uploadAnnouncementFile(fd: FormData) {
    return this.http.post<{ attachmentKey: string; attachmentName: string }>(
      this.base + 'admin/content/announcements/upload', fd).pipe(catchError(this.err));
  }

  // ── Medal tally / results ────────────────────────────────────────────────────
  resultsForEvent(eventId)     { return this.get<any[]>(`results/event/${eventId}`); }
  saveResult(b: any)           { return this.post('results', b); }
  updateResult(id, b)          { return this.put(`results/${id}`, b); }

  // ── QR attendance ────────────────────────────────────────────────────────────
  listSessions()               { return this.get<any[]>('attendance/sessions'); }
  attendanceEvents()           { return this.get<any[]>('attendance/events'); }
  /**
   * /upload is permission-gated. A plain <img> sends no bearer token, so the
   * gate answers with its placeholder avatar instead of the real photo - it
   * looks like a missing file rather than a rejected request. Fetching through
   * HttpClient puts the token on the request via the AuthInterceptor.
   */
  authedImage(url: string) {
    return this.http.get(url, { responseType: 'blob' }).pipe(catchError(this.err));
  }

  /** Absolute URL for a path under the upload root, for use with authedImage. */
  uploadUrl(path: string) { return this.base + 'upload/' + String(path).replace(/^\/+/, ''); }
  createSession(eventId)       { return this.post('attendance/sessions', { eventId }); }
  setSessionActive(id, active) { return this.patch(`attendance/sessions/${id}`, { isActive: active }); }
  sessionLogs(id)              { return this.get<any[]>(`attendance/sessions/${id}/logs`); }
  // Scan flow: decode a student's QR token against a session, then mark them present.
  attendanceLookup(token, sessionId) { return this.post<any>('attendance/lookup', { token, sessionId }); }
  /** `studentIds` is one promoted-row id per SPORT being marked - attendance is per game. */
  attendanceMark(sessionId, studentIds: number[]) { return this.post<any>('attendance/mark', { sessionId, studentIds }); }
  /** Reverses a mark made moments ago, from the undo bar on the scan screen. */
  attendanceUndo(sessionId, studentId) { return this.post<any>('attendance/undo', { sessionId, studentId }); }

  // ── PlayZone quiz builder ────────────────────────────────────────────────────
  listQuizzes()                { return this.get<any[]>('admin/playzone/quizzes'); }
  getQuiz(id)                  { return this.get<any>(`admin/playzone/quizzes/${id}`); }
  saveQuiz(b: any)             { return this.post('admin/playzone/quizzes', b); }
  updateQuiz(id, b)            { return this.put(`admin/playzone/quizzes/${id}`, b); }
  deleteQuiz(id)               { return this.del(`admin/playzone/quizzes/${id}`); }
  addQuestion(quizId, b)       { return this.post(`admin/playzone/quizzes/${quizId}/questions`, b); }
  updateQuestion(id, b)        { return this.put(`admin/playzone/questions/${id}`, b); }
  deleteQuestion(id)           { return this.del(`admin/playzone/questions/${id}`); }

  // ── Media Vault manager ──────────────────────────────────────────────────────
  mvYears()                    { return this.get<string[]>('admin/media-vault/years'); }
  mvAlbums(year?: string, q?: string) {
    const p: string[] = [];
    if (year) p.push(`year=${encodeURIComponent(year)}`);
    if (q)    p.push(`q=${encodeURIComponent(q)}`);
    return this.get<any[]>('admin/media-vault/albums' + (p.length ? `?${p.join('&')}` : ''));
  }
  mvEventGames(eventId: number) { return this.get<any>(`admin/media-vault/albums/${eventId}/games`); }
  /** Events that do not yet have an album - the Add Album picker. */
  mvAvailableEvents(year?: string, q?: string) {
    const p: string[] = [];
    if (year) p.push(`year=${encodeURIComponent(year)}`);
    if (q)    p.push(`q=${encodeURIComponent(q)}`);
    return this.get<any[]>('admin/media-vault/available-events' + (p.length ? `?${p.join('&')}` : ''));
  }
  mvCreateAlbum(eventId: number) { return this.post('admin/media-vault/albums', { eventId }); }
  mvDeleteAlbumRecord(albumId: number) { return this.del(`admin/media-vault/albums/${albumId}`); }
  mvItems(eventId?, gameId?)   {
    const q: string[] = [];
    if (eventId) q.push(`eventId=${eventId}`);
    if (gameId)  q.push(`gameId=${gameId}`);
    return this.get<any[]>('admin/media-vault/items' + (q.length ? `?${q.join('&')}` : ''));
  }
  mvUpload(form: FormData)      { return this.post('admin/media-vault/items', form); }
  /** Same endpoint as mvUpload but streams HttpEvent (UploadProgress/Response) for a per-file progress bar. */
  mvUploadWithProgress(form: FormData): import('rxjs').Observable<HttpEvent<any>> {
    const req = new HttpRequest('POST', this.base + 'admin/media-vault/items', form, { reportProgress: true });
    return this.http.request(req).pipe(catchError(this.err));
  }
  mvRetag(id, b)                { return this.patch(`admin/media-vault/items/${id}`, b); }
  mvDelete(id)                  { return this.del(`admin/media-vault/items/${id}`); }
  mvDeleteAlbum(eventId?, gameId?) {
    const q: string[] = [];
    if (eventId) q.push(`eventId=${eventId}`);
    if (gameId)  q.push(`gameId=${gameId}`);
    return this.del('admin/media-vault/albums' + (q.length ? `?${q.join('&')}` : ''));
  }

  // ── Subscribers ──────────────────────────────────────────────────────────────
  subscribers(q, status, page, limit) {
    return this.get<any>(`admin/subscribers?q=${encodeURIComponent(q || '')}&status=${status}&page=${page}&limit=${limit}`);
  }
  subscriberExportUrl(status)  { return `${this.base}admin/subscribers/export?status=${status}`; }
  setSubscriberStatus(id, status) { return this.patch(`admin/subscribers/${id}`, { status }); }
  deleteSubscriber(id)         { return this.del(`admin/subscribers/${id}`); }

  // ── Payments ───────────────────────────────────────────────────────────────
  // Reconciliation view: unlike the school portal this includes `created`
  // (started, never finished) and `failed` rows, which are the ones worth chasing.
  payments(f: any) {
    const q = Object.keys(f)
      .filter(k => f[k] !== '' && f[k] !== null && f[k] !== undefined && f[k] !== 'all')
      .map(k => `${k}=${encodeURIComponent(f[k])}`)
      .join('&');
    return this.get<any>(`admin/payments${q ? '?' + q : ''}`);
  }
  paymentSummary() { return this.get<any[]>('admin/payments/summary'); }
  checkPayment(id: number)  { return this.post<any>(`admin/payments/${id}/check`, {}); }
  settlePayment(id: number) { return this.post<any>(`admin/payments/${id}/settle`, {}); }
  scanUnrecorded(days: number) { return this.post<any>('admin/payments/scan', { days }); }
  lookupPayment(ref: string, date?: string) {
    return this.get<any>(`admin/payments/lookup?ref=${encodeURIComponent(ref)}${date ? '&date=' + date : ''}`);
  }

  // ── School undertaking (the form schools sign and return) ──────────────────
  undertakingStatus() { return this.get<any>('admin/content/undertaking'); }
  uploadUndertaking(file: File) {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.http.post<any>(this.base + 'admin/content/undertaking', fd).pipe(catchError(this.err));
  }
  undertakingUrl() { return `${this.base}public/school-undertaking`; }
  /**
   * A school's signed undertaking, by registration id. The backend resolves
   * where the file actually lives - the stored value is a full storage key for
   * new uploads but a bare filename for older and legacy-PHP ones, so building
   * the /upload/... path in the browser 404'd for every older application.
   */
  registrationDocument(registrationId: number | string) {
    return this.http.get(`${this.base}schoolRegistration/document/${registrationId}`, { responseType: 'blob' })
      .pipe(catchError(this.err));
  }

  /** The same signed undertaking, by SCHOOL id - what the edit screen knows. */
  schoolUndertaking(schoolId: number | string) {
    return this.http.get(`${this.base}school/${schoolId}/undertaking`, { responseType: 'blob' })
      .pipe(catchError(this.err));
  }

  /** Receipts are authed, so the bearer token must ride on the request - fetched as a blob, not a plain link. */
  paymentReceipt(id: number) {
    return this.http.get(`${this.base}admin/payments/${id}/receipt`, { responseType: 'blob' })
      .pipe(catchError(this.err));
  }

  // ── Invoices. Same list endpoint as receipts: one payment, one invoice. ────
  paymentInvoice(id: number) {
    return this.http.get(`${this.base}admin/payments/${id}/invoice`, { responseType: 'blob' })
      .pipe(catchError(this.err));
  }
  invoiceSummary() { return this.get<any>('admin/payments/invoices/summary'); }
  /** Allocate and store the number without downloading the PDF. */
  issueInvoice(id: number) { return this.post<any>(`admin/payments/${id}/invoice/issue`, {}); }
  issueInvoices(ids: number[]) { return this.post<any>('admin/payments/invoices/issue', { ids }); }
  billingSettings() { return this.get<any>('admin/payments/settings'); }
  saveBillingSettings(body: any) { return this.put<any>('admin/payments/settings', body); }
  invoiceRecipients(id: number) { return this.get<any>(`admin/payments/${id}/invoice/recipients`); }
  sendInvoice(id: number, body: { to?: string; note?: string }) {
    return this.post<any>(`admin/payments/${id}/invoice/send`, body);
  }

  // ── Mail provider (Resend / Sendpository, switchable without a deploy) ─────
  mailSettings()               { return this.get<any>('admin/mail'); }
  setMailProvider(provider: string) { return this.post('admin/mail', { provider }); }
  /** Real send, through whichever provider is passed - not necessarily the active one. */
  testMail(to: string, provider: string) { return this.post<any>('admin/mail/test', { to, provider }); }
  /** Sendpository delivery log. `status` filters to one delivery state. */
  mailActivity(status?: string, limit = 150) {
    const q = `?limit=${limit}` + (status ? `&status=${status}` : '');
    return this.get<any>(`admin/mail/activity${q}`);
  }
  mailActivityDetail(id: string) { return this.get<any>(`admin/mail/activity/${id}`); }

  // ── Affiliation ─────────────────────────────────────────────────────────────
  // ISSO sets what a school pays and for how long; the school portal only reads
  // it. Offline payments and legacy end dates are recorded here too.
  affiliationAmounts()                    { return this.get<any>('admin/affiliation/amounts'); }
  setAffiliationAmounts(b: any)           { return this.put<any>('admin/affiliation/amounts', b); }
  affiliationSchools(status: string, q: string) {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (q) p.set('q', q);
    return this.get<any>(`admin/affiliation/schools?${p.toString()}`);
  }
  affiliationForSchool(schoolId: number)  { return this.get<any>(`admin/affiliation/schools/${schoolId}`); }
  setAffiliationOffer(schoolId: number, b: any)  { return this.put<any>(`admin/affiliation/schools/${schoolId}/offer`, b); }
  recordAffiliation(schoolId: number, b: any)    { return this.post<any>(`admin/affiliation/schools/${schoolId}/record`, b); }
  cancelAffiliation(id: number)           { return this.del<any>(`admin/affiliation/${id}`); }
}
